import { useMutation } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import request from "utils/request";
import { selectAuthToken } from "containers/App/selectors";

/**
 * Extracts error information from API responses.
 *
 * NOTE: The `request` utility (src/utils/request.js) is fetch-based and rejects
 * with the raw parsed JSON body directly (e.g., `{ error: "Forbidden" }`),
 * NOT wrapped in an axios-shaped `{ response: { status, data } }` object.
 *
 * The Rails API returns different error structures:
 * - 4xx errors use `error` (simple client-facing message)
 * - 500 errors use `error_message` + `failed_assets[]` (detailed diagnostics)
 *
 * @param {object} error - Raw JSON body from the rejected API response
 * @returns {object} Normalized error with message, type, and optional details
 */
const parseDownloadError = (error) => {
  const message = error?.error || error?.error_message || error?.message;
  const failedAssets = error?.failed_assets;

  // If the error body includes failed_assets, it's a server-side processing error
  if (failedAssets) {
    return {
      status: 500,
      message: message || "An error occurred while processing your download",
      type: "server_error",
      failedAssets
    };
  }

  if (!message) {
    return {
      status: 500,
      message: "An unexpected error occurred",
      type: "unknown"
    };
  }

  return {
    status: null,
    message,
    type: "api_error"
  };
};

/**
 * Creates a bulk download job for gallery assets using v2 API with batched download support
 *
 * @param {string} authToken - Authentication token
 * @param {object} params - Download parameters
 * @param {string} params.startDate - Start date in YYYY-MM-DD format
 * @param {string} params.endDate - End date in YYYY-MM-DD format
 * @param {number} params.channel_id - Channel ID
 * @returns {Promise} Response containing batch_id, total_chunks, total_assets/asset_count, message
 */
const createDownload = async (authToken, { startDate, endDate, channel_id }) => {
  const options = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Token token=${authToken}`,
    },
    body: JSON.stringify({ start_date: startDate, end_date: endDate, channel_id: channel_id }),
  };

  try {
    return await request("v2/downloads", options);
  } catch (error) {
    throw parseDownloadError(error);
  }
};

/**
 * Hook to create a new bulk download job using v2 API with batched download support
 *
 * The API creates batched downloads that are processed asynchronously.
 * Users receive an email when their download is ready - no polling required.
 *
 * @param {object} options - Optional react-query mutation options (onSuccess, onError, etc.)
 * @returns {object} useMutation result with mutate, isPending, isError, error, and data
 *
 * @example
 * const { mutate, isPending, data, error } = useCreateDownload({
 *   onSuccess: (data) => {
 *     // data.batch_id: Unique identifier for the download batch
 *     // data.total_chunks: Number of ZIP parts (1 for small downloads, >1 for large)
 *     // data.total_assets: Total assets (multi-chunk) OR data.asset_count (single-chunk)
 *     // data.message: Confirmation message from server
 *     console.log('Download initiated:', data);
 *   },
 *   onError: (error) => {
 *     // error.message: User-friendly error message
 *     // error.type: Error category ("api_error", "server_error", "unknown")
 *     // error.failedAssets: Array of failed asset keys (server_error only)
 *     console.error('Failed:', error.message);
 *   }
 * });
 * mutate({ startDate: '2025-01-01', endDate: '2025-01-31', channel_id: 123 });
 */
export const useCreateDownload = (options) => {
  const authToken = useSelector(selectAuthToken());
  return useMutation({
    mutationFn: (params) => createDownload(authToken, params),
    mutationKey: ["createDownload"],
    ...options,
  });
};
